/**
 * 1応答内に複数 propose_menu tool_use が届いた場合のマルチカード表示ラッパー。
 * 追加要件 §1.5 / 設計 §6.3a。tool_use_id を key として N 枚縦積みし、2案以上なら
 * 応答単位の「まとめてお気に入り登録」ボタンを表示する。
 */
import { Sparkles } from 'lucide-react';
import { StructuredProposalCard, type StructuredProposal } from './StructuredProposalCard';

export type ProposalEntry = {
  toolUseId: string;
  messageId: string;
  proposal: StructuredProposal;
};

export function ProposalGroup({
  proposals, industryGroups, industrySections, isGenerating, onFavorite, onRegenerate, regeneratingMessageId,
  onBulkFavorite, bulkFavoritePending, registeredMessageIds, canFavorite = true,
}: {
  proposals: ProposalEntry[];
  industryGroups?: string[];
  industrySections?: string[];
  isGenerating?: boolean;
  onFavorite: (entry: ProposalEntry) => void;
  onRegenerate: (entry: ProposalEntry, servings: number) => void;
  regeneratingMessageId?: string | null;
  onBulkFavorite?: () => void;
  bulkFavoritePending?: boolean;
  /** 個別 or 一括で既に登録済みの message_id (ボタンの無効化に使う) */
  registeredMessageIds?: Set<string>;
  /** ⚠️ ai_menu_favorites:manage権限が無いユーザーには個別/一括のお気に入りボタンを表示しない(§8-184) */
  canFavorite?: boolean;
}) {
  if (proposals.length === 0) return null;
  const unregisteredCount = proposals.filter((p) => !registeredMessageIds?.has(p.messageId)).length;

  return (
    <div className="space-y-3">
      {proposals.map((entry, idx) => (
        <div key={entry.toolUseId}>
          {proposals.length > 1 && <div className="text-xs font-semibold text-text-sub mb-1">案 {idx + 1}</div>}
          <StructuredProposalCard
            proposal={entry.proposal}
            industryGroups={industryGroups}
            industrySections={industrySections}
            isGenerating={isGenerating}
            onFavorite={() => onFavorite(entry)}
            onRegenerate={(servings) => onRegenerate(entry, servings)}
            regenerating={regeneratingMessageId === entry.messageId}
            canFavorite={canFavorite}
          />
        </div>
      ))}
      {canFavorite && proposals.length >= 2 && onBulkFavorite && unregisteredCount > 0 && (
        <button
          type="button"
          onClick={onBulkFavorite}
          disabled={bulkFavoritePending}
          aria-describedby="bulk-favorite-desc"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-brand-red text-brand-red text-sm font-medium hover:bg-brand-red/5 disabled:opacity-50"
        >
          <Sparkles size={16} />
          {bulkFavoritePending ? '登録中…' : `${unregisteredCount}案まとめてお気に入り登録`}
        </button>
      )}
      <span id="bulk-favorite-desc" className="sr-only">未登録の{unregisteredCount}案を一度に登録します</span>
    </div>
  );
}
